/**
 * Every plan this student has held.
 *
 * *** THE REST OF THE PAGE SHOWS ONE SUBSCRIPTION ***
 * `/me/subscription` reports the newest row, because it answers "what can
 * I do right now". A student who had bought four plans saw no trace of the
 * first three, and an abandoned purchase could mask a plan that was still
 * running (raised 2026-10-31).
 *
 * *** PAID-FOR PLANS ONLY ***
 * The server excludes purchases that never completed. Listing those would
 * read as a list of charges for money nobody took.
 */

import { useState } from 'react'
import { ApiError } from '@/lib/api'
import { useMySubscriptionHistory } from '@/lib/queries'
import { formatDate, formatMoney } from '@/lib/format'
import styles from './PlansPage.module.css'

/** How many rows before it folds. The current plan is shown above anyway. */
const FIRST_PAGE = 3

export function SubscriptionHistoryList() {
  const history = useMySubscriptionHistory()
  const [expanded, setExpanded] = useState(false)

  const items = history.data?.items ?? []

  if (history.error) {
    return (
      <p className={styles.error}>
        {history.error instanceof ApiError
          ? history.error.message
          : 'Could not load your past plans.'}
      </p>
    )
  }

  /*
    *** HIDDEN ONLY WHEN THERE IS GENUINELY NOTHING ***
    This used to hide at `items.length <= 1`, reasoning that one row would
    duplicate the card above. That was wrong: the cards above show
    `current_plan` or the LAST ATTEMPT, and an attempt that was never paid
    for is excluded from this list. So a student with exactly one real
    plan and one abandoned purchase saw their plan NOWHERE — which is the
    bug this whole section was built to fix (2026-11-02).

    Still hidden while the answer is outstanding: an empty list is a claim.
  */
  if (items.length === 0) return null

  const shown = expanded ? items : items.slice(0, FIRST_PAGE)

  return (
    <section>
      <h2 className={styles.sectionTitle}>Plans you have had</h2>

      <ul className={styles.history}>
        {shown.map((item) => {
          const cancelled = item.cancelled_at !== null
          return (
            <li key={item.subscription_id} className={styles.historyRow}>
              <span className={styles.historyMain}>
                <span className={styles.historyName}>
                  {item.plan_name ?? 'Plan'}
                  {item.is_current && (
                    <span className={styles.historyNow}>Current</span>
                  )}
                </span>
                <span className={styles.historyWhen}>
                  {formatDate(item.starts_at)}
                  {item.expires_at ? ` – ${formatDate(item.expires_at)}` : ''}
                  {cancelled ? ' · cancelled' : ''}
                </span>
              </span>

              <span className={styles.historyFacts}>
                <span className={styles.historyUsed}>
                  {item.sessions_used} of {item.session_count} used
                </span>
                {/*
                  Null on a plan an admin assigned rather than sold. A zero
                  would be a claim about money that was never charged.
                */}
                {item.charged_amount !== null && (
                  <span className={styles.historyPaid}>
                    {formatMoney(item.charged_amount, item.currency)}
                  </span>
                )}
              </span>
            </li>
          )
        })}
      </ul>

      {items.length > FIRST_PAGE && (
        <button
          type="button"
          className={styles.historyMore}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded
            ? 'Show fewer'
            : `Show all ${items.length}`}
        </button>
      )}
    </section>
  )
}
